import type { IOS_ERROR, SPOT } from '@/types'
import { sleep } from '@/utils/parsers'

export function useSpotSelection() {
  const route = useRoute()
  const router = useRouter()
  const appStore = useAppStore()
  const authStore = useAuthStore()

  const showSpotSelection = ref(false)
  const isChangingSpot = ref(false)

  const spots = ref<SPOT[]>(appStore.spotsForUser ?? [])

  const hasSelectedSpot = computed(() => spots.value.some((s) => s.selected))

  const spotFromSpotInfo = (info = appStore.spotInfo): SPOT[] => [
    {
      id: info.id,
      name: info.name,
      selected: true,
      notificationsEnabled: false,
    },
  ]

  if (!spots.value.length && appStore.spotInfo) {
    spots.value = spotFromSpotInfo()
  }

  if (!hasSelectedSpot.value && !appStore.isAdmin && spots.value.length) {
    spots.value[0].selected = true
  }

  const selectedSpot = computed<SPOT>(() => {
    const spotId = Number(authStore.spotId)
    return (
      spots.value.find((s: SPOT) => s.id == spotId) ?? {
        id: appStore.spotInfo?.id,
        name: appStore.spotInfo?.name,
        selected: true,
        notificationsEnabled: false,
      }
    )
  })

  const openSpotSelection = () => {
    showSpotSelection.value = true
  }

  const restoreCurrentSpot = (previousSpotId: string) => {
    if (appStore.spotInfo?.id) {
      authStore.setSpotId(appStore.spotInfo.id.toString())
      spots.value = spotFromSpotInfo()
    } else if (previousSpotId) {
      authStore.setSpotId(previousSpotId)
    }
  }

  const spotInfoUrlFor = (spotId: SPOT['id'] | string, deviceToken?: string) =>
    deviceToken
      ? `${endpoints().spotInfo(spotId)}?device_id=${deviceToken}`
      : endpoints().spotInfo(spotId)

  const handleAuthFailure = (statusCode: number | null) => {
    if (statusCode !== 401 && statusCode !== 403) return false

    const iosError: IOS_ERROR = { statusCode, action: 'logout' }
    appStore.setIOSError(iosError)
    return true
  }

  const loadSpotInfo = async (
    spotId: SPOT['id'] | string,
    deviceToken?: string,
  ) => {
    const { data, statusCode } = await useApi(
      spotInfoUrlFor(spotId, deviceToken),
    ).json()

    if (handleAuthFailure(statusCode.value)) return null
    if ((statusCode.value && statusCode.value >= 400) || !data.value?.id) {
      return null
    }

    appStore.setSpotInfo(data.value)
    appStore.setSpotTz(data.value.time_zone ?? 'America/Chicago')

    return data.value
  }

  const selectSpotAsAdmin = async (spotId: SPOT['id']) => {
    const previousSpotId = authStore.spotId

    if (!spotId || Number.isNaN(Number(spotId))) {
      restoreCurrentSpot(previousSpotId)
      return false
    }

    isChangingSpot.value = true
    authStore.setSpotId(spotId.toString())

    try {
      const info = await loadSpotInfo(
        authStore.spotId,
        route.query.device_token as string | undefined,
      )

      if (!info) {
        if (appStore.iosError) return false
        restoreCurrentSpot(previousSpotId)
        return false
      }

      authStore.setSpotId(info.id.toString())
      router.replace({
        query: { ...route.query, spot_id: info.id.toString() },
      })
      spots.value = spotFromSpotInfo(info)
      showSpotSelection.value = false

      return true
    } finally {
      isChangingSpot.value = false
    }
  }

  const selectSpot = async (spotId: SPOT['id'], path = '/') => {
    const deviceToken = localStorage.getItem('device-token')

    spots.value.forEach((item) => {
      item.selected = item.id === spotId
    })
    authStore.setSpotId(spotId.toString())

    await loadSpotInfo(spotId, deviceToken ?? undefined)

    router.push(`${path}?spot_id=${spotId}&device_token=${deviceToken}`)
    showSpotSelection.value = false
  }

  const syncAdminSpotInfo = async (newSpotId: string) => {
    if (!appStore.isAdmin) return true

    const info = await loadSpotInfo(
      newSpotId,
      route.query.device_token as string | undefined,
    )

    if (!info) return false

    spots.value = spotFromSpotInfo(info)
    return true
  }

  watch(
    () => appStore.spotsForUser,
    async (newValue) => {
      if (!newValue) return

      if (!newValue.length) {
        await sleep(300)
        if (appStore.spotInfo) {
          spots.value = spotFromSpotInfo()
        }
        return
      }

      spots.value = newValue
    },
  )

  return {
    spots,
    selectedSpot,
    showSpotSelection,
    isChangingSpot,
    openSpotSelection,
    selectSpot,
    selectSpotAsAdmin,
    syncAdminSpotInfo,
  }
}
